import React, { createContext, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Icon } from '../design/Icon';
import { ExportMenu } from '../components/shared/ExportMenu';

export const ShellContext = createContext({ filterSlot: null });

export function useShell() {
  return useContext(ShellContext);
}

/**
 * Page frame inside the app shell: breadcrumbs, title, subtitle, actions and the Export menu,
 * then the page content. `filters` renders page-specific filters into the filter bar.
 *
 * crumbs: [{ label, to? }]
 * exportConfig: { name, endpoint?, params?, columns?, rows?, extraFormats? } (see useExport)
 */
export function Page({ title, subtitle, crumbs, actions, exportConfig, filters, documentTitle, children }) {
  const { filterSlot } = useShell();

  useEffect(() => {
    const previous = document.title;
    document.title = `${documentTitle || title} | Vertolytics`;
    return () => {
      document.title = previous;
    };
  }, [title, documentTitle]);

  return (
    <>
      <div className="page-header" id="vl-header">
        <div className="titles">
          {crumbs && crumbs.length > 0 && (
            <div className="crumbs">
              {crumbs.map((crumb, i) => (
                <React.Fragment key={`${crumb.label}-${i}`}>
                  {i > 0 && <Icon name="chevR" className="ico-sm" />}
                  {crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{crumb.label}</span>}
                </React.Fragment>
              ))}
            </div>
          )}
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-sub">{subtitle}</p>}
        </div>
        <div className="page-actions" id="vl-actions">
          {actions}
          {exportConfig && <ExportMenu {...exportConfig} />}
        </div>
      </div>
      <div className="page-content">{children}</div>
      {filters && filterSlot ? createPortal(filters, filterSlot) : null}
    </>
  );
}
